import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AppShell } from '@/components/AppShell';
import { EmptyState } from '@/components/EmptyState';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Spinner } from '@/components/Spinner';
import { useAuth } from '@/lib/auth';
import { LoginPage } from '@/pages/Login';

// Each page is its own chunk. Initial load only needs the App shell + login;
// admin/org routes pay their own JS cost on first navigation. Drops the
// initial bundle from ~1MB to roughly the shell + Fluent core.
const MePage = lazy(() => import('@/pages/Me').then((m) => ({ default: m.MePage })));
const FollowUpsPage = lazy(() =>
  import('@/pages/FollowUps').then((m) => ({ default: m.FollowUpsPage })),
);
const OrgOverviewPage = lazy(() =>
  import('@/pages/OrgOverview').then((m) => ({ default: m.OrgOverviewPage })),
);
const OrgUsersPage = lazy(() => import('@/pages/OrgUsers').then((m) => ({ default: m.OrgUsersPage })));
const OrgSettingsPage = lazy(() =>
  import('@/pages/OrgSettings').then((m) => ({ default: m.OrgSettingsPage })),
);
const AdminOverviewPage = lazy(() =>
  import('@/pages/AdminOverview').then((m) => ({ default: m.AdminOverviewPage })),
);
const AdminUsersPage = lazy(() =>
  import('@/pages/AdminUsers').then((m) => ({ default: m.AdminUsersPage })),
);
const AdminOrgsPage = lazy(() => import('@/pages/AdminOrgs').then((m) => ({ default: m.AdminOrgsPage })));
const AdminTiersPage = lazy(() =>
  import('@/pages/AdminTiers').then((m) => ({ default: m.AdminTiersPage })),
);
const AdminAuditPage = lazy(() =>
  import('@/pages/AdminAudit').then((m) => ({ default: m.AdminAuditPage })),
);
const AdminFeedbackPage = lazy(() =>
  import('@/pages/AdminFeedback').then((m) => ({ default: m.AdminFeedbackPage })),
);
const AcceptInvitePage = lazy(() =>
  import('@/pages/AcceptInvite').then((m) => ({ default: m.AcceptInvitePage })),
);

export function App() {
  const { status, me } = useAuth();

  if (status === 'loading') {
    return <Spinner label="Loading…" />;
  }

  if (status === 'signed_out' || !me) {
    return <LoginPage />;
  }

  const isSuperAdmin = me.user.role === 'super_admin';

  return (
    <ErrorBoundary>
      <AppShell>
        <Suspense fallback={<Spinner label="Loading…" />}>
          <Routes>
            <Route path="/" element={<Navigate to={isSuperAdmin ? '/admin/overview' : '/me'} replace />} />

            <Route path="/me" element={<MePage />} />
            <Route path="/me/follow-ups" element={<FollowUpsPage />} />

            <Route path="/invite/:token" element={<AcceptInvitePage />} />

            <Route path="/org/:slug" element={<Navigate to="overview" replace />} />
            <Route path="/org/:slug/overview" element={<OrgOverviewPage />} />
            <Route path="/org/:slug/users" element={<OrgUsersPage />} />
            <Route path="/org/:slug/settings" element={<OrgSettingsPage />} />

            <Route path="/admin" element={<Navigate to="/admin/overview" replace />} />
            <Route path="/admin/overview" element={isSuperAdmin ? <AdminOverviewPage /> : <ForbiddenPage />} />
            <Route path="/admin/users" element={isSuperAdmin ? <AdminUsersPage /> : <ForbiddenPage />} />
            <Route path="/admin/orgs" element={isSuperAdmin ? <AdminOrgsPage /> : <ForbiddenPage />} />
            <Route path="/admin/tiers" element={isSuperAdmin ? <AdminTiersPage /> : <ForbiddenPage />} />
            <Route path="/admin/audit" element={isSuperAdmin ? <AdminAuditPage /> : <ForbiddenPage />} />
            <Route path="/admin/feedback" element={isSuperAdmin ? <AdminFeedbackPage /> : <ForbiddenPage />} />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </AppShell>
    </ErrorBoundary>
  );
}

function ForbiddenPage() {
  return (
    <EmptyState
      title="Super-admin only"
      description="This area is for Kiro operators. If you should have access, contact support."
    />
  );
}

function NotFoundPage() {
  return <EmptyState title="Page not found" description="The URL doesn't match any portal route." />;
}
